import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Users, UserPlus, Mail, CheckCircle, Clock, X, Shield } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ClientConsentManagement } from "@/components/clinician/ClientConsentManagement";

interface TherapistConnectionProps {
  userId: string;
}

interface TherapistConnection {
  id: string;
  clinician_id: string;
  status: string;
  accepted_at: string | null;
  invited_at: string;
  clinician_profile?: {
    practice_name: string | null;
    professional_title: string | null;
  };
}

export function TherapistConnection({ userId }: TherapistConnectionProps) {
  const [connections, setConnections] = useState<TherapistConnection[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [selectedClinicianId, setSelectedClinicianId] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      fetchConnections();
    }
  }, [userId]);

  const fetchConnections = async () => {
    try {
      setLoading(true);

      // Fetch active connections
      const { data: clientData, error: clientError } = await supabase
        .from('clinician_clients')
        .select(`
          id,
          clinician_id,
          status,
          accepted_at,
          invited_at
        `)
        .eq('client_id', userId);

      if (clientError) throw clientError;

      // Fetch clinician profiles for connections
      if (clientData && clientData.length > 0) {
        const clinicianIds = clientData.map(c => c.clinician_id);
        const { data: profiles } = await supabase
          .from('clinician_profiles')
          .select('user_id, practice_name, professional_title')
          .in('user_id', clinicianIds);

        const connectionsWithProfiles = clientData.map(conn => ({
          ...conn,
          clinician_profile: profiles?.find(p => p.user_id === conn.clinician_id)
        }));

        setConnections(connectionsWithProfiles);
      } else {
        setConnections([]);
      }

      // Fetch pending invitations sent TO this user
      const { data: invitations } = await supabase
        .from('clinician_invitations')
        .select('*')
        .eq('recipient_id', userId)
        .eq('status', 'pending');

      setPendingInvitations(invitations || []);

    } catch (error) {
      console.error('Error fetching connections:', error);
      toast.error('Failed to load therapist connections');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteTherapist = async () => {
    if (!inviteEmail) {
      toast.error('Please enter your therapist\'s email address');
      return;
    }

    setInviting(true);
    try {
      const { error } = await supabase.functions.invoke('send-clinician-invitation', {
        body: {
          recipient_email: inviteEmail,
          message: inviteMessage,
          invitation_type: 'client_to_clinician'
        }
      });

      if (error) throw error;

      toast.success('Invitation sent to your therapist');
      setIsInviteOpen(false);
      setInviteEmail('');
      setInviteMessage('');
      fetchConnections();
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase.functions.invoke('accept-clinician-invitation', {
        body: { invitation_token: invitationId }
      });

      if (error) throw error;

      toast.success('Connection accepted');
      fetchConnections();
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      toast.error(error.message || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('clinician_invitations')
        .update({ status: 'declined', responded_at: new Date().toISOString() })
        .eq('id', invitationId);

      if (error) throw error;

      toast.success('Invitation declined');
      fetchConnections();
    } catch (error: any) {
      console.error('Error declining invitation:', error);
      toast.error('Failed to decline invitation');
    }
  };

  const activeConnections = connections.filter(c => c.status === 'active');
  const pendingConnections = connections.filter(c => c.status === 'pending');

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            My Therapist Connections
          </CardTitle>
          <CardDescription>
            Connect with your therapist to share your wellbeing data and receive better support
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invite Therapist */}
          <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <UserPlus className="h-4 w-4" />
                Invite My Therapist
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Your Therapist</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Send an invitation to your therapist to connect on Therappi. Once connected, you can choose what data to share with them.
                </p>
                <div className="space-y-2">
                  <Label>Therapist's Email</Label>
                  <Input 
                    type="email"
                    placeholder="therapist@example.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message (optional)</Label>
                  <Textarea 
                    placeholder="Add a personal message..."
                    value={inviteMessage}
                    onChange={e => setInviteMessage(e.target.value)}
                    rows={3}
                  />
                </div>
                <Button 
                  onClick={handleInviteTherapist} 
                  disabled={inviting || !inviteEmail}
                  className="w-full"
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Pending Invitations FROM Therapists */}
          {pendingInvitations.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-foreground flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                Pending Invitations
              </h3>
              {pendingInvitations.map(invitation => (
                <div 
                  key={invitation.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-primary/10 border border-primary/20"
                >
                  <div>
                    <p className="font-medium">{invitation.sender_type === 'clinician' ? 'Therapist Invitation' : 'Connection Request'}</p>
                    <p className="text-sm text-muted-foreground">
                      Received {format(new Date(invitation.created_at), 'MMM d, yyyy')}
                    </p>
                    {invitation.message && (
                      <p className="text-sm mt-2 italic">"{invitation.message}"</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleAcceptInvitation(invitation.invitation_token)}>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Accept
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDeclineInvitation(invitation.id)}>
                      <X className="h-4 w-4 mr-1" />
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pending Connections */}
          {pendingConnections.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-medium text-foreground flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Pending Connections
              </h3>
              {pendingConnections.map(connection => (
                <div 
                  key={connection.id}
                  className="flex items-center gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20"
                >
                  <Users className="h-5 w-5 text-amber-500" />
                  <div className="flex-1">
                    <p className="font-medium">
                      {connection.clinician_profile?.practice_name || 'Therapist'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Awaiting response • Invited {format(new Date(connection.invited_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <Badge variant="outline">Pending</Badge>
                </div>
              ))}
            </div>
          )}

          {/* Active Connections */}
          {activeConnections.length > 0 ? (
            <div className="space-y-3">
              <h3 className="font-medium text-foreground flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Connected Therapists
              </h3>
              {activeConnections.map(connection => (
                <div 
                  key={connection.id}
                  className="flex items-center gap-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20"
                >
                  <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">
                      {connection.clinician_profile?.practice_name || 'Therapist'}
                    </p>
                    {connection.clinician_profile?.professional_title && (
                      <p className="text-sm text-muted-foreground">
                        {connection.clinician_profile.professional_title}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Connected since {connection.accepted_at && format(new Date(connection.accepted_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setSelectedClinicianId(connection.clinician_id)}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      Manage Sharing
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            pendingInvitations.length === 0 && pendingConnections.length === 0 && (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">No therapist connections yet</p>
                <p className="text-sm text-muted-foreground">Invite your therapist to start sharing your wellbeing data</p>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Data Sharing Management Dialog */}
      {selectedClinicianId && (
        <Dialog open={!!selectedClinicianId} onOpenChange={() => setSelectedClinicianId(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Data Sharing</DialogTitle>
            </DialogHeader>
            <ClientConsentManagement 
              clinicianId={selectedClinicianId} 
              isClientView={true}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}